import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommandStatus } from "@lookiero/messaging-react";
import { PaymentFlow, PaymentFlowStrategies, PaymentPayload, Section } from "@lookiero/payments-front";
import { LegacyBoxCheckoutStrategyPayload } from "@lookiero/payments-front/build/components/PaymentFlow/internals/strategies/LegacyBoxCheckoutStrategy";
import { useLogger } from "@lookiero/sty-psp-logging";
import { NotificationLevel, useCreateToastNotification } from "@lookiero/sty-psp-notifications";
import { CheckoutProjection } from "../../../projection/checkout/checkout";
import {
  ViewCheckoutBookingById,
  viewCheckoutBookingById,
  ViewCheckoutBookingByIdResult,
} from "../../../projection/checkoutBooking/viewCheckoutBookingById";
import { MESSAGING_CONTEXT_ID } from "../../delivery/baseBootstrap";
import { useSubmitCheckout } from "../../domain/checkout/react/useSubmitCheckout";
import { useReconciliateCheckoutBooking } from "../../domain/checkoutBooking/react/useReconciliateCheckoutBooking";
import { useViewPaymentFlowPayloadByCheckoutId } from "../../projection/payment/react/useViewPaymentFlowPayloadByCheckoutId";
import { useViewPricingByCheckoutId } from "../../projection/pricing/react/useViewPricingByCheckoutId";
import { CheckoutTrackingEvent } from "../../tracking/tracking";
import { useTrackCheckout } from "../../tracking/useTrackCheckout";
import { I18nMessages } from "../i18n/i18n";
import { Routes } from "../routing/routes";
import { usePaymentInstrumentEvents } from "./usePaymentInstrumentEvents";
import { useQueryBus } from "./useQueryBus";
import { useStaticInfo } from "./useStaticInfo";

type CheckoutFlowStatus = "idle" | "loading" | "success" | "error";

interface CheckoutFlowFunction {
  (): Promise<void>;
}

type CheckoutFlowReturn = [
  checkoutFlow: CheckoutFlowFunction,
  checkoutFlowStatus: CheckoutFlowStatus,
  paymentFlow: ReactNode,
];

interface OnSuccessFunctionArgs {
  readonly checkoutTrackingEvent: CheckoutTrackingEvent;
}

interface OnSuccessFunction {
  (args: OnSuccessFunctionArgs): void;
}

interface UseCheckoutFlowArgs {
  readonly checkout: CheckoutProjection | undefined;
  readonly getAuthToken: () => Promise<string>;
  readonly onSuccess: OnSuccessFunction;
}

interface UseCheckoutFlowFunction {
  (args: UseCheckoutFlowArgs): CheckoutFlowReturn;
}

const useCheckoutFlow: UseCheckoutFlowFunction = ({ checkout: checkoutProjection, getAuthToken, onSuccess }) => {
  const logger = useLogger();
  const queryBus = useQueryBus();
  const {
    customer: { customerId, country, segment, name, email },
    basePath,
    tradename,
  } = useStaticInfo();
  const paymentFlowRef = useRef<PaymentFlowStrategies>(null);
  const [paymentFlowPayload] = useViewPaymentFlowPayloadByCheckoutId({
    checkoutId: checkoutProjection?.id as string,
  });
  const [pricing] = useViewPricingByCheckoutId({
    checkoutId: checkoutProjection?.id,
    queryOptions: { refetchOnMount: true },
  });
  const [submitCheckout, submitCheckoutStatus] = useSubmitCheckout({
    checkoutId: checkoutProjection?.id,
    logger,
  });
  const [createNotification] = useCreateToastNotification({ contextId: MESSAGING_CONTEXT_ID, logger });
  const [reconciliate] = useReconciliateCheckoutBooking({
    checkoutBookingId: checkoutProjection?.checkoutBookingId,
    checkoutId: checkoutProjection?.id,
    logger,
  });
  const [checkoutBookingExpired, setCheckoutBookingExpired] = useState(false);
  const [startLegacyBoxCheckoutStatus, setStartLegacyBoxCheckoutStatus] = useState<CheckoutFlowStatus>("idle");
  const [authToken, setAuthToken] = useState<string>();
  useEffect(() => {
    const loadAuthToken = async () => setAuthToken(await getAuthToken());
    loadAuthToken();
  }, [getAuthToken]);
  const trackCheckout = useTrackCheckout({
    checkout: checkoutProjection,
    pricing,
    userId: customerId,
    country,
    segment,
    tradename,
  });

  const onPaymentSuccess = useCallback(async () => {
    setStartLegacyBoxCheckoutStatus("success");

    const submitted = await submitCheckout();

    // submitCheckout already reports its own error (toast + submitCheckoutStatus turning into
    // CommandStatus.ERROR, which checkoutFlowStatus already folds into "error"): bail out here so a
    // failed submit never shows the success toast/modal, leaving the confirmation screen recoverable.
    if (!submitted) {
      return;
    }

    createNotification({
      bodyI18nKey: I18nMessages.CHECKOUT_TOAST_PAYMENT_SUCCESS,
      level: NotificationLevel.SUCCESS,
    });

    const checkoutTrackingEvent = trackCheckout();
    onSuccess({ checkoutTrackingEvent });
  }, [createNotification, onSuccess, submitCheckout, trackCheckout]);

  const checkoutFlow: CheckoutFlowFunction = useCallback(async () => {
    // The box was already charged (startLegacyBoxCheckoutStatus flips to "success" only inside
    // onPaymentSuccess, right before submitCheckout runs): a retry within this same mount must
    // re-run ONLY the submit, never paymentFlowRef.current.startLegacyBoxCheckout again, or the
    // customer gets charged twice for the same box. This guard lives in React state, so ANY unmount
    // of the Checkout route element resets it - its own back button to Summary just as much as a
    // reload; that gap is preexisting (see CheckoutMiddleware) and out of scope here.
    if (startLegacyBoxCheckoutStatus === "success") {
      await onPaymentSuccess();
      return;
    }

    if (checkoutProjection?.checkoutBookingId) {
      const checkoutBooking = await queryBus<ViewCheckoutBookingById, ViewCheckoutBookingByIdResult>(
        viewCheckoutBookingById({ checkoutBookingId: checkoutProjection?.checkoutBookingId }),
      );

      if (checkoutBooking?.isExpired) {
        reconciliate();
        setCheckoutBookingExpired(true);
        return;
      }
    }

    setStartLegacyBoxCheckoutStatus("loading");

    await paymentFlowRef.current?.startLegacyBoxCheckout({
      ...paymentFlowPayload,
      userInformation: { email, name },
      returnUrl: `${basePath}/${Routes.CHECKOUT}`,
    } as unknown as LegacyBoxCheckoutStrategyPayload);
  }, [
    startLegacyBoxCheckoutStatus,
    onPaymentSuccess,
    checkoutProjection?.checkoutBookingId,
    paymentFlowPayload,
    email,
    name,
    basePath,
    queryBus,
    reconciliate,
  ]);
  const onPaymentError = useCallback(
    (payload: PaymentPayload) => {
      setStartLegacyBoxCheckoutStatus("error");

      createNotification({
        bodyI18nKey: payload.metadata?.toaster?.id || I18nMessages.CHECKOUT_TOAST_PAYMENT_ERROR,
        level: NotificationLevel.ERROR,
      });
    },
    [createNotification],
  );
  usePaymentInstrumentEvents({ onSuccess: onPaymentSuccess, onError: onPaymentError });

  const checkoutFlowStatus: CheckoutFlowStatus = useMemo(() => {
    if (startLegacyBoxCheckoutStatus === "loading" || submitCheckoutStatus === CommandStatus.LOADING) {
      return "loading";
    }

    if (startLegacyBoxCheckoutStatus === "success" && submitCheckoutStatus === CommandStatus.SUCCESS) {
      return "success";
    }

    if (
      startLegacyBoxCheckoutStatus === "error" ||
      submitCheckoutStatus === CommandStatus.ERROR ||
      checkoutBookingExpired
    ) {
      return "error";
    }

    return "idle";
  }, [startLegacyBoxCheckoutStatus, submitCheckoutStatus, checkoutBookingExpired]);

  const paymentFlow = useMemo(
    () => (authToken ? <PaymentFlow ref={paymentFlowRef} section={Section.BOX_CHECKOUT} /> : null),
    [authToken],
  );

  return useMemo(
    () => [checkoutFlow, checkoutFlowStatus, paymentFlow],
    [checkoutFlow, paymentFlow, checkoutFlowStatus],
  );
};

export type { CheckoutFlowStatus, OnSuccessFunction };
export { useCheckoutFlow };
